/**
 * StickyHeaderBar component module.
 * @module investor/shared/components/StickyHeaderBar
 *
 */
import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import styled, {withTheme} from 'styled-components/macro';
import {fill} from '../../../shared/components/cssUtils';
import {Button, Column, DescriptiveText, Row} from '../../../shared';
import IconDropdown from '../../../shared/components/IconDropdown';
import debug from 'debug';

const log = debug('circleblack:StickyHeaderBar');

const breakpoints = {
  md: 669,
};

const mediaQueries = (key) => style => `@media (max-width: ${breakpoints[key]}px) {${style}}`;

const _HeaderBar = styled.div`
    height: ${props => props.height || 'fit-content'};
    min-height: 50px;
    ${props => fill(props.width || 'fill')};
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: sticky;
    position: -webkit-sticky;
    top: 70px;
    z-index: 8;
    box-sizing: border-box;
    background-color: ${props => props.theme.tileBackgroundColor};
    box-shadow: 2px 2px 5px 2px rgba(0, 0, 0, 0.4), 
                1px 1px 0 0 rgba(255, 255, 255, 0.2) inset, 
                -1px -1px 0 0 rgba(0, 0, 0, 0.5) inset;
    border-radius: 12px;
    margin: 10px;
    padding: 5px;
    white-space: nowrap;
`;

const _ButtonsWrapper = styled.div`
  ${mediaQueries('md')` {
    display: none;
  }`}
`;

/**
 * Sticky Header Bar component
 * @name StickyHeaderBar
 */
const StickyHeaderBar = (props) => {
  const {width, height, title, subTitle, buttons, dropdownOptions} = props;

  const attributes = {height, width};
  const [windowSize, setWindowSize] = useState('');

  const handleWindowSize = () => {
    if (window.innerWidth <= breakpoints.md) {
      setWindowSize('sm');
    } else {
      setWindowSize('md');
    }
  };

  const getUpdatedOptions = () => {
    const setOfOptions = [
      ...(!!dropdownOptions?.mainSection?.options ? dropdownOptions.mainSection.options : []),
      (buttons?.primary && buttons.primary),
      (buttons?.secondary && buttons.secondary),
      (buttons?.additional && buttons.additional),
    ].filter(o => !!o);

    return {
      ...dropdownOptions,
      ...(!!setOfOptions.length && {
        mainSection: {
          options: setOfOptions,
        },
      }),
    };
  };

  const options = windowSize === 'sm' ? getUpdatedOptions() : dropdownOptions;

  useEffect(() => {
    handleWindowSize();
    window.addEventListener('resize', handleWindowSize);
    return function cleanup() {
      window.removeEventListener('resize', handleWindowSize);
    };
  }, []);

  log('rendering');
  return <_HeaderBar {...attributes}>
    <Row justifySubRow={'left'} width={'fit-content'}>
      <Column width={'fit-content'} height={'fit-content'} alignItems={'left'}>
        <DescriptiveText
            fontSize={subTitle ? '14px' : '18px'}
            font={subTitle ? 'ProximaRegular' : 'ProximaSemibold'}
            alignment={'left'}
            margin={'0 10px 0 10px'}
            width={'fit-content'}>
          {title}
        </DescriptiveText>
        {subTitle
            && <DescriptiveText
            fontSize={'18px'}
            font={'ProximaSemibold'}
            alignment={'left'}
            margin={'0 10px 0 10px'}
            width={'fit-content'}>
              {subTitle}
            </DescriptiveText>
        }
      </Column>
      {(buttons?.additional)
          && <_ButtonsWrapper>
            <Column width={'fit-content'} alignItems={'right'}>
              <Button size={'sm'}
                      importance={'secondary'}
                      width={'120px'}
                      label={buttons.additional.label}
                      action={buttons.additional.action}
                      form={buttons.additional.form}
                      disabled={buttons.additional.isDisabled}/>
            </Column>
          </_ButtonsWrapper>}
    </Row>
    <Row justifySubRow={'right'} width={'fit-content'} height={'40px'}>
      <_ButtonsWrapper>
        {buttons?.secondary
          && <Button size={'md'}
                     importance={'secondary'}
                     width={'145px'}
                     label={buttons.secondary.label}
                     action={buttons.secondary.action}
                     form={buttons.secondary.form}
                     disabled={buttons.secondary.isDisabled}/>
        }
        {buttons?.primary
          && <Button size={'md'}
                     importance={'primary'}
                     width={'145px'}
                     label={buttons.primary.label}
                     action={buttons.primary.action}
                     form={buttons.primary.form}
                     disabled={buttons.primary.isDisabled}/>
        }
      </_ButtonsWrapper>
      {!!options?.mainSection && <IconDropdown icon={'threeDot'} options={options} position={{right: -5}}/>}
    </Row>
  </_HeaderBar>;
};

export default withTheme(StickyHeaderBar);

StickyHeaderBar.propTypes = {
  /**
   * The theme prop provided by the ThemeProvider
   */
  theme: PropTypes.object,

  /**
   * The height of the sticky header bar
   */
  height: PropTypes.string,

  /**
   * The width of the sticky header bar
   */
  width: PropTypes.string,

  /**
   * Title of the sticky header bar
   */
  title: PropTypes.string,

  /**
   * Sub Title of the sticky header bar
   */
  subTitle: PropTypes.string,

  /**
   * Buttons of the sticky header bar
   */
  buttons: PropTypes.shape({
    additional: PropTypes.shape({
      label: PropTypes.string.isRequired,
      action: PropTypes.oneOfType([PropTypes.func, PropTypes.string]).isRequired,
      form: PropTypes.string,
      isDisabled: PropTypes.bool,
    }),
    primary: PropTypes.shape({
      label: PropTypes.string.isRequired,
      action: PropTypes.oneOfType([PropTypes.func, PropTypes.string]).isRequired,
      form: PropTypes.string,
      isDisabled: PropTypes.bool,
    }),
    secondary: PropTypes.shape({
      label: PropTypes.string.isRequired,
      action: PropTypes.oneOfType([PropTypes.func, PropTypes.string]).isRequired,
      form: PropTypes.string,
      isDisabled: PropTypes.bool,
    }),
  }),

  /**
   * IconDropdown options object
   */
  dropdownOptions: PropTypes.shape({
    /**
     * Options main section
     */
    mainSection: PropTypes.shape({
      /**
       * Section label
       */
      label: PropTypes.string,
      /**
       * Section options
       */
      options: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string,
        action: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
        form: PropTypes.string,
        value: PropTypes.string,
        isDisabled: PropTypes.bool,
      })).isRequired,
    }).isRequired,
    /**
     * Options secondary section
     */
    secondarySection: PropTypes.shape({
      /**
       * Section label
       */
      label: PropTypes.string,
      /**
       * Section options
       */
      options: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string,
        action: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
        form: PropTypes.string,
        value: PropTypes.string,
        isDisabled: PropTypes.bool,
      })).isRequired,
    }),
  }),
};
